import {
  responsiveMediaFragment,
  responsiveMediaTransparentFragment
} from '../mediaFragments.gql'
import {
  bannerRecord,
  multiColumnRecord,
  videoRecord,
  richTextRecord,
  seoFragment,
  customisedProductFragment,
  commerceTagFragment,
  productCategoryFragment,
  styledBannerRecord,
  dataCaptureBlockRecord,
  linkBlock,
  cardFragment,
  cardScrollerRowRecord
} from '../fragments.gql.js'

export const swatchFragment = `
  id
  styles
  image {
    src: url(imgixParams: {h: "50"})
  }
  strapImage {
    src: url
    responsiveImage {
      srcSet
      webpSrcSet
      sizes
      src
      width
      height
      aspectRatio
      alt
      title
    }
  }
`

export const partSpecificationFragment = `
  title
  tooltip
  value
`

export const optionTechConfig = `
  tech
  icon
  displayTitle
  displaySubtitle
  abbe
  material {
    ${partSpecificationFragment}
  }
  lensTechSelectorTitleOverride
  tagText
  tagColour
  extendedContent {
    value
  }
`

export const productOptionFragment = `
  scmId
  scmStagingId
  description
  optionTech {
    ${optionTechConfig}
  }
`

export const productHeroDesignFragment = ({ includeAlgoliaFields }) => `
  productHeroDesigns {
    enabled
    parts {
      sku
    }
    ${includeAlgoliaFields ? filterTagsFragment
      : ''}
    ${includeAlgoliaFields ? 'tagText'
      : ''}
    swatchMode
  }
`

export const filterTagsFragment = `
  filterTags {
    filterValue
    displayValue
    belongsToFilterGroup {
      filterGroupValue
      filterKey
      displayKey
    }
  }
`

export const optionFragment = ({ includeAdFeedFields }) => `
  id
  name
  fulfilmentName
  partType
  sku
  vlt
  swatchStyle {
    ${swatchFragment}
  }
  productOptions {
    ${productOptionFragment}
  }
  productNameOverride
  cartDetailsOverride {
    partName: partNameOverride
    choiceName: optionNameOverride
  }
  ${responsiveMediaFragment('lifestyleImagesOverride')}
  leaderSkuOverride {
    sku
  }
  isLimitedEdition
  additionalProductSKU: additionalProductSku
  tags {
    ... on CommerceTagRecord {
      ${commerceTagFragment}
    }
  },
  optionTech: partTech {
    ${optionTechConfig}
  }
  material {
    ${partSpecificationFragment}
  }
  buttonToolTipTitle
  buttonToolTipContent
  ${
    includeAdFeedFields
      ? `
  adFeedBackfilledParts {
    id
    sku
  }
  `
      : ''
  }
`

export const partFragment = ({
  includeDisabledOptions = false,
  includeAdFeedFields,
}) => `
  id
  skuPrefix
  name
  verboseName
  icon
  imageLayer
  excludeFromAdFeeds
  excludeFromProductDetails
  excludeFromShopifyCheckoutImages
  disableInfiniteSwatchIcon
  followerOf {
    skuPrefix
  }
  options {
    ${optionFragment({ includeAdFeedFields })}
  }
  material {
    ${partSpecificationFragment}
  }
  additionalSpec {
    ${partSpecificationFragment}
  }
  specificationType
  ${includeDisabledOptions ? `disabledOptions { ${optionFragment({})} }` : ''}
  ${
    includeAdFeedFields
      ? `
  adFeedBackfillDefaultPart {
    sku
  }
  adFeedFollowerOf {
    skuPrefix
  }
  `
      : ''
  }
`

const upsellsFragment = `
  upsells: _allReferencingUpsells {
    id
    key
    mode
    name
    showForProducts {
      id
    }
    upsellProduct {
      ... on BaseProductRecord {
        id
        _modelApiKey
        sku
      }
      ... on ProductRecord {
        id
        _modelApiKey
        sku
        baseProduct {
          sku
        }
      }
    }
    upsellProducts {
      sku
    }
    leadPart {
      skuPrefix
    }
  }
`

const lineItemContentFragment = `
  lineItemContent {
    title
    content {
      blocks
      value
      links
    }
  }
`

export const getBaseProductFragment = ({
  includePDPConfig = false,
  depth = 1,
  includeDisabledOptions = false,
  includeAdFeedFields,
  includeAlgoliaFields = false,
}) => `
  id
  sku
  name
  subTitle
  baseProduct {
    id
    sku
    name
  }
  shopifyPrebuiltProduct
  productInfoTitle
  productInfoText
  hasPdp
  url
  pageMode
  icon
  parts {
    ${partFragment({ includeDisabledOptions, includeAdFeedFields })}
  }
  frameTypeOverride
  useBaseProductName
  preOrderCopyOverride
  lensNameOverride
  relatedProducts {
    slug
    breadcrumbLabel
    breadcrumbTarget
    products {
      sku
      icon
      url
      name
      subTitle
    }
  }
  lensChoiceOverride {
    ${optionFragment({})}
  }
  staticLensTech {
    ${optionTechConfig}
  }
  ${responsiveMediaFragment(
    'lifestyleImagesOverride',
    '{ w: "1400", h: "1400" }'
  )}
  swatchIconOverride {
    url(imgixParams: {w: "50", h: "50"})
  }
  swappableParts
  ${includePDPConfig ? pdpConfigQuery({
    includeRows: depth > 0
  }) : ''}
  ${productHeroDesignFragment({ includeAlgoliaFields })}
  googleProductCategory
  ${responsiveMediaTransparentFragment('limitedEditionLogo')}
  logoWidth
  hideLensTech
  categories {
    ... on ProductCategoryRecord {
      ${productCategoryFragment}
    }
  }
  showPackagingOptions
  defaultPackagingOption
  enableVirtualTryOn
  oosUnlessInvited
  invitationSegment {
    splitTestKey
    variantId
  }
  showReviews
  useGenericReviews
  tagText
  tagTheme
  promoMessaging {
    enabled
    ctaSuffix
    tagText
    theme {
      themeType
    }
  }
  includeInProductFeed
  _allReferencingProductReviewAggregations {
    averageStars
    count
  }
  promotionProductGroup {
    giftSelectorTitle
    disclaimer
    additionalMessage
    giftedProduct {
      sku
      stockQtyBuffer
      litePackaging
      groupId 
    }
  }
  ${upsellsFragment}
  ${lineItemContentFragment}
  ${includeAlgoliaFields ? algoliaFieldsFragment
  : ''}
`

const algoliaFieldsFragment = `
  ${filterTagsFragment}
  hideWhenOutOfStock
  hideOnPlps
  limitedEditions {
    customisedProduct {
      ${customisedProductFragment}
      parts {
        sku
      }
      ${filterTagsFragment}
      hideWhenOutOfStock
      hideOnPlps
      baseProduct {
        sku
        _allReferencingProductReviewAggregations {
          averageStars
          count
        }
        useGenericReviews
      }
    }
  }
`




const pdpConfigQuery = ({
  includeRows = true,
  includeAlgoliaFields = false
}) => `
  ${seoFragment}
  pdpText
  sizeAndFit
  designImagesOrder
  hideSwatches
  buttonToolTipTitle
  buttonToolTipContent
  stockIndicatorThreshold
  stockIndicatorThresholdText
  stockIndicatorSoldOutText
  showDefaultInTheBoxProduct
  inTheBoxItems {
    id
    title
    description
    image {
      url(imgixParams: {w: "400" h: "400", fit: crop, crop: focalpoint})
    }
  }
  quotesSectionTitle
  quotes {
    ${responsiveMediaFragment('image')}
    quote
  }
  techSpecs {
    id
    label
    linkedIcon {
      icon
    }
    icon
    value
    lensTechFilter
  }
  lensGuideBanner {
    ${responsiveMediaFragment('image')}
  }
  lensPreviewScenes {
    name: scene
    icon
  }
  showLensPreviewIcons
  lensPreviewConditions
  ${
    includeRows
      ? `
  rowsAbove {
    ${bannerRecord}
    ${dataCaptureBlockRecord}
    ${styledBannerRecord}
  }
  rowsBelow {
    ${bannerRecord}
    ${videoRecord}
    ${richTextRecord}
    ${multiColumnRecord}
  }
  `
      : ''
  }
  secondaryProduct {
    sku
    name
    subTitle
  }
  limitedEditions {
    isCustomisable
    customisedProduct {
      ${customisedProductFragment}
      baseProduct {
        ${getBaseProductFragment({
          includePDPConfig: false,
          depth: 0,
          includeAlgoliaFields
        })}
      }
    }
  }
  limitedEditionDescription
  theme
  promoCapsuleCase {
    sku
  }
  additionalProductSKU: additionalProductSku
  showPackagingOptions
  defaultPackagingOption
  ctaSuffix
  showRegisterInterestForm
  registerInterestFormCopy
  secondaryCtaLink {
    ${linkBlock}
  }
  secondaryCtaLinkText
  accordion {
    id
    title
    accordionType
    heading
    tag
    content {
      value
    }
  }
  showProductInfoTextBlock
  ${responsiveMediaTransparentFragment('staticProductImages')}
  imageShowcaseBackgrounds {
    url
  }
  vlt
  frameSpecification {
    ${partSpecificationFragment}
  }
  lensShape
  lensChange
  weight
  heightFrame
  heightLenses
  lengthFrame
  widthLenses
  ${responsiveMediaFragment(
    'technicalImageHero',
    '{ w: "1400", h: "1400" }'
  )}
  ${responsiveMediaFragment(
    'technicalImageFront',
    '{ w: "800", h: "800" }'
  )}
  ${responsiveMediaFragment(
    'technicalImageSide',
    '{ w: "800", h: "800" }'
  )}
  sampleReview: _allReferencingCustomerReviews(
    first: "1"
    filter: {rating: {eq: "5"}, body: {isBlank: false}, title: {isBlank: false}}
  ) {
    title
    displayName
    body
    country
    rating
  }
`

export const getLifestyleImageQuery = (productId) => `query {
  allProductLifestyleImages(
    filter: {product: {eq: "${productId}"}},
    first: "100"
  ) {
    id
    optionSku1
    optionSku2
    ${responsiveMediaFragment('image', '{ ar: "1", crop: faces, fit: crop }')}
  }
}`

export const productAttributeFragment = `
id
productAttributeCategories {
  categoryHeading
  productAttributeCategorySections {
    sectionHeading
    sectionTooltip
    categoryAttributes {
      ... on CircularRatingRecord {
        type: _modelApiKey
        id
        rating
        label
        icon {
          icon
        }
      }
      ... on DualValueRatingRecord {
        type: _modelApiKey
        id
        leftLabel
        middleLabel
        rightLabel
        firstValue
        secondValue
      }
      ... on SingleValueRatingRecord {
        type: _modelApiKey
        id
        label
        rating
      }
    }
  }
}
`

export const pdpPageContentFragment = `
  productAccordions {
    id
    title
    accordionType
    heading
    tag
    cards {
      ${cardFragment}
    }
    content {
      blocks
      links
      value
    }
    productAttribute {
        ${productAttributeFragment}
    }
    keyFeaturesTitle
    keyFeaturesTooltip
    conditionsTitle
    conditionsTooltip
  }
  sections {
    ${cardScrollerRowRecord}
    ${videoRecord}
  }
`

export const getPDPPageContent = `query getPDPPageContentQuery($productId: [ItemId!]) {
  allPdpPageContents(
    filter: { products: { anyIn: $productId } }
  ) {
    ${pdpPageContentFragment}
  }
}`

export default ({
  skus,
  includePDPConfig,
  includeDisabledOptions = false,
  includeAdFeedFields = false,
  includeAlgoliaFields = false,
  datoLocale = 'en',
  pageSize = 50
}) => `query {
  allBaseProducts(fallbackLocales: en, locale: ${datoLocale}, filter: {sku: {in: ${JSON.stringify(skus)}}}, first: "${pageSize}") {
    ${getBaseProductFragment({
      includePDPConfig,
      depth: 1,
      includeDisabledOptions,
      includeAdFeedFields,
      includeAlgoliaFields
    })}
  }
}`
